import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

/**
 * Shared topbar for /calendar routes (BW10).
 *  - View switcher (Day · Week · Month · Year)
 *  - Prev / Today / Next nav
 *  - "+" New event
 *  - Keyboard shortcuts: ← → for prev/next, T for today, D/W/M/Y for views,
 *    N to fire onNewEvent.
 */

export type CalendarView = "day" | "week" | "month" | "year";

export function CalendarToolbar({
  title,
  view,
  isoDay,
  isoWeek,
  isoMonth,
  isoYear,
  onPrev,
  onNext,
  onToday,
  onNewEvent,
}: {
  title: string;
  view: CalendarView;
  isoDay: string;
  isoWeek: string;
  isoMonth: string;
  isoYear: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onNewEvent?: () => void;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't intercept when the user is typing in a form.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      switch (k) {
        case "arrowleft":
          e.preventDefault();
          onPrev();
          return;
        case "arrowright":
          e.preventDefault();
          onNext();
          return;
        case "t":
          onToday();
          return;
        case "d":
          navigate(`/calendar/day/${isoDay}`);
          return;
        case "w":
          navigate(`/calendar/week/${isoWeek}`);
          return;
        case "m":
          navigate(`/calendar/month/${isoMonth}`);
          return;
        case "y":
          navigate(`/calendar/year/${isoYear}`);
          return;
        case "n":
          if (onNewEvent) onNewEvent();
          return;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, onPrev, onNext, onToday, onNewEvent, isoDay, isoWeek, isoMonth, isoYear]);

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        flexWrap: "wrap",
        margin: "var(--space-2) 0 var(--space-6)",
      }}
    >
      <h1
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: 0,
        }}
      >
        {title}
      </h1>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        <ViewSwitcher view={view} isoDay={isoDay} isoWeek={isoWeek} isoMonth={isoMonth} isoYear={isoYear} />
        <span style={{ width: 1, height: 24, background: "var(--hair)" }} />
        <NavBtn ariaLabel="Previous" onClick={onPrev}><ChevronLeft size={16} strokeWidth={1.6} /></NavBtn>
        <button
          type="button"
          onClick={onToday}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-chip)",
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          Today
        </button>
        <NavBtn ariaLabel="Next" onClick={onNext}><ChevronRight size={16} strokeWidth={1.6} /></NavBtn>
        {onNewEvent && (
          <button
            type="button"
            onClick={onNewEvent}
            title="New event (N)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 12px",
              background: "var(--accent)",
              color: "#0a0b0d",
              border: "none",
              borderRadius: "var(--radius-chip)",
              fontSize: "var(--type-body-s)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={14} strokeWidth={2} /> New
          </button>
        )}
      </div>
    </header>
  );
}

function ViewSwitcher({
  view,
  isoDay,
  isoWeek,
  isoMonth,
  isoYear,
}: {
  view: CalendarView;
  isoDay: string;
  isoWeek: string;
  isoMonth: string;
  isoYear: string;
}) {
  const items: Array<{ k: CalendarView; label: string; to: string }> = [
    { k: "day", label: "Day", to: `/calendar/day/${isoDay}` },
    { k: "week", label: "Week", to: `/calendar/week/${isoWeek}` },
    { k: "month", label: "Month", to: `/calendar/month/${isoMonth}` },
    { k: "year", label: "Year", to: `/calendar/year/${isoYear}` },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: "inline-flex",
        background: "var(--bg-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-chip)",
        padding: 2,
      }}
    >
      {items.map((it) => {
        const active = it.k === view;
        return (
          <Link
            key={it.k}
            to={it.to}
            role="tab"
            aria-selected={active}
            style={{
              padding: "6px 12px",
              fontSize: "var(--type-body-s)",
              borderRadius: "var(--radius-chip)",
              color: active ? "var(--fg)" : "var(--fg-3)",
              background: active ? "var(--bg)" : "transparent",
              textDecoration: "none",
              fontWeight: active ? 500 : 400,
            }}
          >
            {it.label}
          </Link>
        );
      })}
    </div>
  );
}

function NavBtn({ ariaLabel, onClick, children }: { ariaLabel: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: "var(--radius-chip)",
        background: "transparent",
        border: "1px solid var(--hair-2)",
        color: "var(--fg-2)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </button>
  );
}
